// packages/schema/src/feed-configs.ts
/**
 * Feed configs + runs schema — per-Site inventory ingest configuration and
 * its run history. See docs/specs/generic-feed-pipeline.md §4.1.
 *
 * Every row is site-scoped — all queries must pass through withSite().
 */
import { sql, desc } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { sites } from "./tables";

export const feedTransportEnum = pgEnum("feed_transport", ["sftp", "https", "s3", "upload"]);
export const feedScheduleEnum = pgEnum("feed_schedule", ["daily-4am", "manual-only"]);
export const feedRunStatusEnum = pgEnum("feed_run_status", [
  "running",
  "success",
  "partial",
  "failed",
]);
export const feedTriggerEnum = pgEnum("feed_trigger", ["schedule", "manual", "webhook", "replay"]);

/** Encrypted blob — shape depends on transport. Decrypted only inside Inngest steps. */
export type TransportConfig = { ciphertext: string; nonce: string };

/** Filter rule for multi-tenant feeds carrying multiple lots/dealers in one file. */
export type RowFilter = {
  column: string;
  op: "equals" | "regex" | "in";
  value: string | string[];
};

export type FeedRunError = {
  rowIndex?: number;
  vin?: string;
  vehicleId?: string;
  severity: "warn" | "error";
  message: string;
  context?: Record<string, unknown>;
};

export type FeedRunSummary = {
  created: number;
  updated: number;
  archived: number;
  errorCount: number;
};

export const feedConfigs = pgTable(
  "feed_configs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    presetId: text("preset_id").notNull(),
    label: text("label").notNull(),
    transport: feedTransportEnum("transport").notNull(),
    transportConfig: jsonb("transport_config").$type<TransportConfig>().notNull(),
    rowFilter: jsonb("row_filter").$type<RowFilter>(),
    schedule: feedScheduleEnum("schedule").notNull().default("manual-only"),
    enabled: boolean("enabled").notNull().default(true),
    lastRunAt: timestamp("last_run_at", { withTimezone: true }),
    lastRunStatus: feedRunStatusEnum("last_run_status"),
    lastRunSummary: jsonb("last_run_summary").$type<FeedRunSummary>(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteIdx: index("feed_configs_site_idx").on(t.siteId),
    siteEnabledIdx: index("feed_configs_site_enabled_idx").on(t.siteId, t.enabled),
  }),
);

export const feedRuns = pgTable(
  "feed_runs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    feedConfigId: uuid("feed_config_id")
      .notNull()
      .references(() => feedConfigs.id, { onDelete: "cascade" }),
    startedAt: timestamp("started_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    status: feedRunStatusEnum("status").notNull().default("running"),
    triggeredBy: feedTriggerEnum("triggered_by").notNull(),
    recordsParsed: integer("records_parsed").notNull().default(0),
    recordsCreated: integer("records_created").notNull().default(0),
    recordsUpdated: integer("records_updated").notNull().default(0),
    recordsArchived: integer("records_archived").notNull().default(0),
    errors: jsonb("errors")
      .$type<FeedRunError[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    replayAttempt: integer("replay_attempt").notNull().default(0),
  },
  (t) => ({
    siteStartedIdx: index("feed_runs_site_started_idx").on(t.siteId, desc(t.startedAt)),
    configStartedIdx: index("feed_runs_config_started_idx").on(t.feedConfigId, desc(t.startedAt)),
  }),
);

export type FeedConfig = typeof feedConfigs.$inferSelect;
export type NewFeedConfig = typeof feedConfigs.$inferInsert;
export type FeedRun = typeof feedRuns.$inferSelect;
export type NewFeedRun = typeof feedRuns.$inferInsert;
